import React, { useEffect } from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';

import { AlertList, ErrorResponse, ModalCard, Spinner } from '~components';
import { AlertMessage } from '~components/form-fields/inputs';
import { TableView } from '~components/table-view-v3';

import columns from '~support-at-home/forms/budgets/bulkCopyList';

import { useGetBulkCopyEstimateQuery } from '~support-at-home/actions/budgets';

interface BulkCopyFilters {
  date_range?: { gte?: string; lte?: string };
  client_ids?: number[];
  funding_type_ids?: number[];
  status_ids?: string[];
}

interface ComponentProps {
  value: BulkCopyFilters;
  onChange?: (name: string, value: any) => any;
}

const ListAndConfirm: React.FC<ComponentProps> = ({ value, onChange }) => {
  const { date_range, client_ids, funding_type_ids, status_ids } = value;

  const { data, isFetching, error } = useGetBulkCopyEstimateQuery(
    {
      start_date: date_range?.gte || '',
      end_date: date_range?.lte || '',
      client_ids,
      funding_type_ids,
      status_ids,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !date_range?.gte || !date_range?.lte,
    },
  );

  // Update budget_ids when budgets data changes
  useEffect(() => {
    if (onChange && data) {
      const budgetIds = data.map((budget) => budget.id);
      onChange('budget_ids', budgetIds);
    }
  }, [data, onChange]);

  if (isFetching) return <Spinner loading={isFetching} />;

  if (error) return <ErrorResponse message={getErrorMessage(error)} />;

  if (!data) return null;

  if (data.length === 0) {
    return (
      <AlertMessage
        id="no-budgets-alert"
        name="no-budgets-alert"
        value="No budgets found, please adjust search criteria and try again."
        type="alert-info"
        color="info"
      />
    );
  }

  return (
    <div>
      <ModalCard header={`${data?.length} Found Budgets`} className="w-100 mb-3">
        <TableView id="bulk-copy-estimate-table" data={data} columns={columns} readonly striped />
      </ModalCard>
      <AlertList
        header="Please Note! These copies will not account for:"
        color="warning"
        listItems={['Outdated price plans, client packages or funding types', 'Deleted or inactive clients']}
      />
    </div>
  );
};

export default ListAndConfirm;
